import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { LoaderCircle } from "lucide-react";

export type SpinnerVariants =
  | "primary"
  | "secondary"
  | "danger"
  | "white"
  | "ai";

type SpinnerProps = {
  variant: SpinnerVariants;
  size?: number;
  layoutClassName?: string;
  UNSAFE_className?: string;
  testId?: string;
  "aria-hidden"?: boolean;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  role?: string;
};

const Spinner = ({
  variant,
  size = 16,
  "aria-hidden": ariaHidden,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  role,
  testId,
  layoutClassName,
  UNSAFE_className,
}: SpinnerProps) => {
  const cn = twMerge(
    "animate-spin",
    variant === "primary" && "text-primary",
    variant === "secondary" && "text-default",
    variant === "white" && "text-white",
    variant === "danger" && "text-danger",
    variant === "ai" && "text-transparent",
    layoutClassName,
    UNSAFE_className,
  );

  if (variant === "ai") {
    return (
      <div
        className={twMerge("animate-spin", layoutClassName, UNSAFE_className)}
        style={{ width: size, height: size }}
        data-testid={testId}
        aria-hidden={ariaHidden}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        role={role}
      >
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient
              id="ai-spinner-gradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#FDD37E" />
              <stop offset="50%" stopColor="#F34D72" />
              <stop offset="100%" stopColor="#7C7FBA" />
            </linearGradient>
          </defs>
          <path
            d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z"
            fill="url(#ai-spinner-gradient)"
          />
        </svg>
      </div>
    );
  }

  return (
    <LoaderCircle
      size={size}
      className={cn}
      data-testid={testId}
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      role={role}
    />
  );
};

export { Spinner };
